/**
 * @file number-input.ts
 * @description Числа общего поля `NumberSliderField` (phase19.16, партия SB120
 * «свобода выбора», этап 0; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §2): разбор ввода
 * «1,1» / «1.1» / «24 px», ограничение min/max с подписью «Наибольшее — 200 px»,
 * округление к шагу, перевод «хранимое ↔ показанное» (`display.factor`,
 * например доля 0,4 ↔ 40 %), вывод с запятой и слова единиц для скринридера
 * («24 пикселя»). Чистые функции — без React, тестируются отдельно.
 *
 * Единицы — реестр `UNIT_META`: новая единица = запись, не ветка в коде.
 */

/** Единица поля (§2): px / % / мс / с / × / em / vh или без единицы. */
export type NumberUnit = "px" | "%" | "ms" | "s" | "×" | "em" | "vh" | "";

/** Как показывать число: хранимое × factor = показанное, знаков после запятой. */
export interface NumberDisplay {
  factor: number;
  decimals: number;
}

export const DEFAULT_DISPLAY: NumberDisplay = { factor: 1, decimals: 0 };

interface UnitMeta {
  /** Подпись рядом с полем. */
  short: string;
  /** Слова для скринридера: 1 пиксель / 2 пикселя / 5 пикселей; null — без слова. */
  words: readonly [string, string, string] | null;
  /** Что за единица — для `aria-describedby`. */
  describe: string;
}

/** Реестр единиц: подпись, склонение и пояснение. */
export const UNIT_META: Readonly<Record<NumberUnit, UnitMeta>> = {
  px: { short: "px", words: ["пиксель", "пикселя", "пикселей"], describe: "в пикселях" },
  "%": { short: "%", words: ["процент", "процента", "процентов"], describe: "в процентах" },
  ms: { short: "мс", words: ["миллисекунда", "миллисекунды", "миллисекунд"], describe: "в миллисекундах" },
  // SB120 этап 3б: смена объявлений 2–30 с.
  s: { short: "с", words: ["секунда", "секунды", "секунд"], describe: "в секундах" },
  "×": { short: "×", words: null, describe: "множитель" },
  em: { short: "em", words: ["эм", "эм", "эм"], describe: "в долях размера шрифта (em)" },
  vh: { short: "vh", words: ["процент высоты экрана", "процента высоты экрана", "процентов высоты экрана"], describe: "в процентах высоты экрана" },
  "": { short: "", words: null, describe: "число" },
};

/** Округление без хвостов плавающей точки (0.1 + 0.2 → 0.3). */
export function roundTo(value: number, decimals: number): number {
  const k = 10 ** decimals;
  return Math.round(value * k) / k;
}

/** Число знаков после запятой у шага (0.05 → 2, 1 → 0). */
export function stepDecimals(step: number): number {
  const text = String(step);
  const dot = text.indexOf(".");
  return dot < 0 ? 0 : text.length - dot - 1;
}

/** Хранимое → показанное (доля 0,4 → 40). */
export function toDisplay(stored: number, display: NumberDisplay): number {
  return roundTo(stored * display.factor, display.decimals);
}

/** Показанное → хранимое (40 → 0,4); 6 знаков снимают хвосты деления. */
export function toStored(shown: number, display: NumberDisplay): number {
  return roundTo(shown / display.factor, 6);
}

/**
 * Разбор ввода: пробелы и единица в конце отбрасываются, запятая = точка.
 * «1,1» → 1.1, «24 px» → 24, «40 %» → 40; пусто и нечисло → null.
 */
export function parseNumberInput(text: string): number | null {
  const cleaned = text
    // Пробелы; \s в JS ловит и неразрывный (копия из подписи «40 %»).
    .replace(/\s/g, "")
    .replace(",", ".")
    .replace("−", "-")
    // Единица в конце: px, %, мс, ×, em, vh, x (латинская вместо ×).
    .replace(/(px|%|мс|ms|×|x|em|vh)$/i, "");
  if (cleaned === "" || !/^[+-]?(\d+\.?\d*|\.\d+)$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** Ближайшее значение на шаге от min (сетка min, min + step, …), в пределах. */
export function snapToStep(value: number, min: number, max: number, step: number): number {
  const snapped = min + Math.round((value - min) / step) * step;
  const decimals = Math.max(stepDecimals(step), stepDecimals(min));
  return Math.min(max, Math.max(min, roundTo(snapped, decimals)));
}

/** Вывод числа с запятой, лишние нули справа убраны: 1.50 → «1,5». */
export function formatNumber(value: number, decimals: number): string {
  return String(roundTo(value, decimals)).replace(".", ",").replace("-", "−");
}

/** «24 px», «40 %», «1,5 ×», «13» — для подписей и сообщений. */
export function formatWithUnit(value: number, decimals: number, unit: NumberUnit): string {
  const short = UNIT_META[unit].short;
  return short ? `${formatNumber(value, decimals)} ${short}` : formatNumber(value, decimals);
}

/** Слово единицы по числу: дробное — «1,5 пикселя», целое — по правилу 1/2–4/5+. */
function unitWord(value: number, words: readonly [string, string, string]): string {
  if (!Number.isInteger(value)) return words[1];
  const n = Math.abs(value) % 100;
  const last = n % 10;
  if (n >= 11 && n <= 14) return words[2];
  if (last === 1) return words[0];
  if (last >= 2 && last <= 4) return words[1];
  return words[2];
}

/** Текст значения для скринридера: «24 пикселя», «40 процентов», «1,33». */
export function speakValue(value: number, decimals: number, unit: NumberUnit): string {
  const words = UNIT_META[unit].words;
  const shown = formatNumber(value, decimals);
  return words ? `${shown} ${unitWord(roundTo(value, decimals), words)}` : shown;
}

/** Пределы поля в показанных единицах. */
export interface ShownRange {
  min: number;
  max: number;
  step: number;
  decimals: number;
  unit: NumberUnit;
}

/** Итог ввода: число (возможно поправленное, с подписью) или «не число». */
export type InputResolution =
  | { kind: "ok"; value: number; note: string | null }
  | { kind: "invalid"; note: string };

/** Знаков после запятой у числа, введённого руками (QA SB120 этап 1, D1). */
export const TYPED_DECIMALS = 2;

/**
 * Ввод в поле → показанное число в пределах. Нечисло — «Введите число от A
 * до B» (поле вернёт прежнее); за пределом — поправка и подпись
 * «Наибольшее — B px» / «Наименьшее — A px». К шагу ввод не тянется (шаг —
 * у ползунка и стрелок): 30,72 остаётся 30,72, 7,5 — 7,5; округление — до
 * `decimals` знаков (по умолчанию 2; целое поле — 0).
 */
export function resolveNumberInput(text: string, range: ShownRange, decimals: number = TYPED_DECIMALS): InputResolution {
  const parsed = parseNumberInput(text);
  const { min, max, unit } = range;
  if (parsed === null) {
    return { kind: "invalid", note: `Введите число от ${formatNumber(min, range.decimals)} до ${formatNumber(max, range.decimals)}` };
  }
  let note: string | null = null;
  if (parsed > max) note = `Наибольшее — ${formatWithUnit(max, range.decimals, unit)}`;
  else if (parsed < min) note = `Наименьшее — ${formatWithUnit(min, range.decimals, unit)}`;
  return { kind: "ok", value: roundTo(Math.min(max, Math.max(min, parsed)), decimals), note };
}

/**
 * Обязательное число схемы (`dim`, `opacity`) в поле с «↺»: равное умолчанию —
 * показываем как наследуемое (`undefined`, справа серым «по умолчанию»), иначе
 * своё. Обратно — `value ?? fallback`. Хранимые данные не меняются.
 */
export function ownValue(value: number, fallback: number): number | undefined {
  return Math.abs(value - fallback) < 1e-9 ? undefined : value;
}
